import React, { useId } from 'react';

/**
 * Piccola ciambella SVG che rappresenta la distribuzione delle VM per stato.
 * Grafica utile e leggera (nessuna dipendenza), non decorativa: le proporzioni
 * riflettono i conteggi reali. Accessibile via role/aria e legenda testuale.
 */

const SEGMENTS = [
  { key: 'on', label: 'Accese', color: '#10b981' }, // emerald-500
  { key: 'off', label: 'Spente', color: '#9ca3af' }, // gray-400
  { key: 'suspended', label: 'Sospese', color: '#f59e0b' }, // amber-500
  { key: 'unknown', label: 'Sconosciute', color: '#d1d5db' }, // gray-300
];

export default function VmDistributionChart({ counts }) {
  const titleId = useId();
  const c = counts || {};
  const data = SEGMENTS.map((s) => ({ ...s, value: Number(c[s.key] || 0) }));
  const total = data.reduce((sum, d) => sum + d.value, 0);

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const stroke = 14;

  // Costruzione dei segmenti come archi (stroke-dasharray).
  let offset = 0;
  const arcs = data
    .filter((d) => d.value > 0)
    .map((d) => {
      const fraction = total > 0 ? d.value / total : 0;
      const dash = fraction * circumference;
      const arc = {
        ...d,
        dasharray: `${dash} ${circumference - dash}`,
        dashoffset: -offset,
      };
      offset += dash;
      return arc;
    });

  return (
    <div className="flex items-center gap-5">
      <svg
        viewBox="0 0 100 100"
        className="h-28 w-28 shrink-0 -rotate-90"
        role="img"
        aria-labelledby={titleId}
      >
        <title id={titleId}>
          Distribuzione delle {total} VM per stato di alimentazione
        </title>
        {/* Anello di base */}
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeWidth={stroke}
          className="stroke-gray-200 dark:stroke-gray-700"
        />
        {total > 0 &&
          arcs.map((a) => (
            <circle
              key={a.key}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              stroke={a.color}
              strokeWidth={stroke}
              strokeDasharray={a.dasharray}
              strokeDashoffset={a.dashoffset}
            />
          ))}
        {/* Totale al centro (contro-ruotato per restare dritto) */}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          dominantBaseline="central"
          transform="rotate(90 50 50)"
          className="fill-gray-800 dark:fill-gray-100"
          style={{ fontSize: '18px', fontWeight: 700 }}
        >
          {total}
        </text>
      </svg>

      <ul className="space-y-1.5 text-sm">
        {data.map((d) => (
          <li key={d.key} className="flex items-center gap-2">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: d.color }}
              aria-hidden="true"
            />
            <span className="text-gray-600 dark:text-gray-300">{d.label}</span>
            <span className="font-semibold text-gray-800 dark:text-gray-100">
              {d.value}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
